/**
 * Year 13 Computer Studies - Yearly Planner 2025
 * Based on Vanuatu National Curriculum Framework
 * Organized by Strands with detailed lesson plans
 */

export const year13ComputerStudiesPlanner = {
  subject: "Computer Studies",
  yearLevel: 13,
  year: 2025,
  school: "St Patrick's College",
  teacher: "Mr. Roy Tari",
  
  courseOverview: {
    description: "Year 13 Computer Studies prepares students for advanced ICT careers and further education.",
    totalWeeks: 40,
    termsPerYear: 4,
    weeksPerTerm: 10,
    periodsPerWeek: 5,
    totalPeriods: 200
  },

  learningOutcomes: [
    "Apply advanced programming concepts to solve complex problems",
    "Design and implement relational database systems",
    "Configure and troubleshoot computer networks",
    "Understand cybersecurity principles and best practices",
    "Develop web applications using modern technologies",
    "Analyze emerging technologies and their societal impact"
  ],

  assessmentWeighting: {
    "Practical Work": 40,
    "Theory Tests": 30,
    "Projects": 20,
    "Participation": 10
  },

  // Organized by Strands (like the lesson plans page)
  strands: [
    {
      id: "strand1",
      name: "Strand 1: Programming",
      description: "Advanced programming concepts and software development",
      color: "blue",
      units: [
        {
          id: "unit1.1",
          unit: "Unit 1.1",
          title: "Object-Oriented Programming Fundamentals",
          weeks: "Weeks 1-3",
          duration: "6 Sessions (45-60 min each)",
          linkedToScheme: true,
          linkedToBlueprint: true,
          sessions: [
            {
              id: "session1",
              number: 1,
              title: "Introduction to Object-Oriented Programming",
              duration: "45-60 minutes",
              structure: [
                { phase: "Introduction and Brainstorming", time: "10 min" },
                { phase: "Concept Explanation (OOP vs Procedural)", time: "15 min" },
                { phase: "Guided Practice (Creating First Class)", time: "20 min" },
                { phase: "Wrap-up and Reflection", time: "10 min" }
              ],
              introduction: [
                "Begin by reviewing procedural programming concepts from Year 12.",
                "Ask students: 'How do you organize a large program with many functions?'",
                "Introduce the concept of OOP as a way to organize code around 'objects' that represent real-world entities."
              ],
              mainActivities: [
                {
                  name: "Concept Explanation",
                  steps: [
                    "Compare procedural programming (sequence of functions) vs OOP (objects with data and behavior).",
                    "Use real-world analogy: A 'Car' object has attributes (color, model) and behaviors (start, stop, accelerate).",
                    "Introduce key OOP terminology: Class, Object, Attribute, Method.",
                    "Show a simple Python class example on the projector."
                  ]
                },
                {
                  name: "Guided Practice",
                  steps: [
                    "Students open Python IDLE or VS Code.",
                    "Together, create a simple 'Student' class with attributes: name, age, year_level.",
                    "Add a method: introduce() that prints student details.",
                    "Create an object (instance) of the Student class.",
                    "Call the introduce() method on the object."
                  ]
                }
              ],
              materials: [
                "Computers with Python installed",
                "Projector for code demonstration",
                "Handout: OOP Terminology Reference Sheet",
                "Whiteboard for diagrams"
              ],
              assessment: [
                "Observation: Student engagement during explanation",
                "Quick Check: Students successfully create a Student object",
                "Exit Ticket: Define class and object in your own words"
              ],
              homework: [
                "Create a 'Teacher' class with at least 3 attributes and 1 method.",
                "Write down 3 real-world objects and identify their potential attributes and methods."
              ],
              teacherNotes: [
                "Use relatable examples from school context (Student, Teacher, Class).",
                "Emphasize that OOP helps manage complexity in large programs.",
                "Be prepared to help students with Python syntax errors."
              ],
              differentiation: [
                "**For struggling learners:** Provide pre-written code templates. Pair with confident learners.",
                "**For advanced learners:** Challenge them to add validation to attributes or create multiple methods."
              ]
            },
            {
              id: "session2",
              number: 2,
              title: "Constructors and the __init__ Method",
              duration: "45-60 minutes",
              structure: [
                { phase: "Homework Review", time: "10 min" },
                { phase: "Understanding Constructors", time: "15 min" },
                { phase: "Practical Exercise", time: "20 min" },
                { phase: "Summary & Assignment", time: "10 min" }
              ],
              introduction: [
                "Review homework: Have students share their Teacher class creations.",
                "Discuss: 'How do we set initial values for an object when it's created?'",
                "Introduce the constructor concept: A special method that runs when an object is created."
              ],
              mainActivities: [
                {
                  name: "Understanding Constructors",
                  steps: [
                    "Explain that __init__ is Python's constructor method.",
                    "The 'self' parameter refers to the object being created.",
                    "Demonstrate: class Student: def __init__(self, name, age): ...",
                    "Show how to create objects with initial values: student1 = Student('John', 15)"
                  ]
                },
                {
                  name: "Practical Exercise",
                  steps: [
                    "Students modify their Student class to use __init__.",
                    "Add parameters for name, age, and year_level.",
                    "Create at least 3 different Student objects with different values.",
                    "Add a method that displays a formatted student ID card."
                  ]
                }
              ],
              materials: [
                "Computers with Python",
                "Projector",
                "Handout: __init__ Method Syntax Guide"
              ],
              assessment: [
                "Code Review: Students demonstrate working __init__ methods",
                "Practical Test: Create a specified class with constructor"
              ],
              homework: [
                "Complete the BankAccount challenge if not finished.",
                "Create a 'Book' class with title, author, pages, and methods."
              ],
              teacherNotes: [
                "Common errors: forgetting 'self', incorrect indentation.",
                "Emphasize that __init__ runs automatically when creating objects."
              ],
              differentiation: [
                "**For struggling learners:** Provide skeleton code with blanks to fill in.",
                "**For advanced learners:** Implement default parameter values in __init__."
              ]
            },
            {
              id: "session3",
              number: 3,
              title: "Encapsulation and Data Protection",
              duration: "45-60 minutes",
              structure: [
                { phase: "Review Previous Concepts", time: "10 min" },
                { phase: "Introduction to Encapsulation", time: "15 min" },
                { phase: "Implementing Private Attributes", time: "20 min" },
                { phase: "Discussion & Summary", time: "10 min" }
              ],
              introduction: [
                "Quick quiz: Define class, object, __init__.",
                "Problem scenario: 'What if someone sets a negative balance in BankAccount?'",
                "Introduce encapsulation: Protecting data by controlling access."
              ],
              mainActivities: [
                {
                  name: "Introduction to Encapsulation",
                  steps: [
                    "Explain the concept: Bundling data and methods that operate on that data.",
                    "Discuss why we need to restrict direct access to some attributes.",
                    "Introduce naming conventions: _protected and __private in Python."
                  ]
                },
                {
                  name: "Implementing Private Attributes",
                  steps: [
                    "Modify BankAccount to use __balance (private).",
                    "Try to access __balance directly - observe the error.",
                    "Create getter and setter methods: get_balance(), set_balance(amount).",
                    "Add validation in setter: prevent negative balances."
                  ]
                }
              ],
              materials: [
                "Computers with Python",
                "Projector",
                "Handout: Encapsulation Concepts"
              ],
              assessment: [
                "Code Check: Students implement private attributes correctly",
                "Quiz: Multiple choice on encapsulation concepts"
              ],
              homework: [
                "Add encapsulation to the Book class from previous homework."
              ],
              teacherNotes: [
                "Python's 'private' is name-mangling, not true privacy.",
                "Focus on the concept rather than implementation details."
              ],
              differentiation: [
                "**For struggling learners:** Focus on single underscore (_protected) first.",
                "**For advanced learners:** Research and explain Python's name mangling mechanism."
              ]
            },
            {
              id: "session4",
              number: 4,
              title: "Inheritance - Creating Child Classes",
              duration: "45-60 minutes",
              structure: [
                { phase: "Warm-up Discussion", time: "10 min" },
                { phase: "Understanding Inheritance", time: "15 min" },
                { phase: "Creating Subclasses", time: "20 min" },
                { phase: "Reflection", time: "10 min" }
              ],
              introduction: [
                "Discussion: 'In real life, children inherit traits from parents.'",
                "Introduce inheritance: Creating new classes based on existing classes.",
                "Show hierarchy: Animal -> Dog, Cat, Bird"
              ],
              mainActivities: [
                {
                  name: "Understanding Inheritance",
                  steps: [
                    "Explain parent (base/super) class and child (derived/sub) class.",
                    "Child classes inherit attributes and methods from parent.",
                    "Syntax: class Dog(Animal): ...",
                    "Demonstrate with simple Animal/Dog example."
                  ]
                },
                {
                  name: "Creating Subclasses",
                  steps: [
                    "Create a Person class with name, age, and introduce() method.",
                    "Create Student subclass that adds year_level and school.",
                    "Create Teacher subclass that adds subject and years_experience.",
                    "Use super().__init__() to call parent constructor."
                  ]
                }
              ],
              materials: [
                "Computers with Python",
                "Projector",
                "Inheritance hierarchy diagrams"
              ],
              assessment: [
                "Diagram: Draw an inheritance hierarchy for vehicles",
                "Code: Create a working inheritance example"
              ],
              homework: [
                "Create a Shape parent class with Triangle and Rectangle child classes."
              ],
              teacherNotes: [
                "Use visual diagrams to show inheritance relationships.",
                "Emphasize code reuse as a key benefit."
              ],
              differentiation: [
                "**For struggling learners:** Provide parent class code, have them create one child class.",
                "**For advanced learners:** Implement multiple levels of inheritance."
              ]
            },
            {
              id: "session5",
              number: 5,
              title: "Polymorphism in Action",
              duration: "45-60 minutes",
              structure: [
                { phase: "Homework Review", time: "10 min" },
                { phase: "Understanding Polymorphism", time: "15 min" },
                { phase: "Implementing Polymorphism", time: "20 min" },
                { phase: "Assessment Prep", time: "10 min" }
              ],
              introduction: [
                "Review Shape classes from homework.",
                "Introduce polymorphism: 'many forms' - same method name, different behaviors."
              ],
              mainActivities: [
                {
                  name: "Understanding Polymorphism",
                  steps: [
                    "Explain: Different classes can have methods with the same name.",
                    "The correct method is called based on the object's type.",
                    "Example: All animals can speak(), but dogs bark and cats meow."
                  ]
                },
                {
                  name: "Implementing Polymorphism",
                  steps: [
                    "Create Animal parent class with speak() method.",
                    "Create Dog, Cat, Cow child classes, each overriding speak().",
                    "Loop through a list of animals and call speak() on each."
                  ]
                }
              ],
              materials: [
                "Computers with Python",
                "Projector",
                "OOP concepts summary sheet"
              ],
              assessment: [
                "Code demonstration: Working polymorphism example",
                "Written: Explain polymorphism with an original example"
              ],
              homework: [
                "Complete any unfinished class exercises.",
                "Prepare a mind map of all OOP concepts covered."
              ],
              teacherNotes: [
                "Polymorphism can be abstract - use many concrete examples.",
                "Review all OOP concepts before assessment."
              ],
              differentiation: [
                "**For struggling learners:** Provide code templates to modify.",
                "**For advanced learners:** Research abstract classes and interfaces."
              ]
            },
            {
              id: "session6",
              number: 6,
              title: "Unit 1.1 Assessment - OOP Programming Test",
              duration: "60 minutes",
              structure: [
                { phase: "Assessment Instructions", time: "5 min" },
                { phase: "Practical Assessment", time: "50 min" },
                { phase: "Submission", time: "5 min" }
              ],
              introduction: [
                "Review assessment rules and expectations.",
                "Clarify any questions before starting."
              ],
              mainActivities: [
                {
                  name: "Practical Assessment",
                  steps: [
                    "Part A: Create specified classes with given requirements (8 marks).",
                    "Part B: Implement inheritance and method overriding (8 marks).",
                    "Part C: Add encapsulation and error handling (7 marks).",
                    "Part D: Create a small working program using all concepts (7 marks)."
                  ]
                }
              ],
              materials: [
                "Assessment paper",
                "Computers with Python",
                "No internet access during assessment"
              ],
              assessment: [
                "Total Marks: 30",
                "Part A: 8 marks",
                "Part B: 8 marks",
                "Part C: 7 marks",
                "Part D: 7 marks"
              ],
              homework: [
                "None - assessment day"
              ],
              teacherNotes: [
                "Ensure fair assessment conditions.",
                "Be available for clarification questions only."
              ],
              differentiation: [
                "Extra time for students with documented needs."
              ]
            }
          ]
        },
        {
          id: "unit1.2",
          unit: "Unit 1.2",
          title: "Data Structures and Algorithms",
          weeks: "Weeks 4-6",
          duration: "6 Sessions (45-60 min each)",
          linkedToScheme: true,
          linkedToBlueprint: true,
          sessions: [
            {
              id: "session1_1.2",
              number: 1,
              title: "Introduction to Data Structures",
              duration: "45-60 minutes",
              structure: [
                { phase: "Introduction", time: "10 min" },
                { phase: "Arrays and Lists", time: "20 min" },
                { phase: "Practical Exercises", time: "15 min" },
                { phase: "Summary", time: "10 min" }
              ],
              introduction: [
                "Discuss: How do we organize and store data efficiently?",
                "Introduce data structures as ways to organize data for specific purposes.",
                "Overview of common data structures: Arrays, Lists, Stacks, Queues."
              ],
              mainActivities: [
                {
                  name: "Arrays and Lists",
                  steps: [
                    "Define array/list as a collection of elements stored in contiguous memory.",
                    "Python lists are dynamic arrays.",
                    "Operations: access by index, append, insert, remove, search.",
                    "Time complexity introduction: O(1) access, O(n) search."
                  ]
                },
                {
                  name: "Practical Exercises",
                  steps: [
                    "Create a list of student names.",
                    "Perform various operations: add, remove, find, sort.",
                    "Measure time for operations on small vs large lists."
                  ]
                }
              ],
              materials: [
                "Computers with Python",
                "Projector",
                "Data structures visual diagrams"
              ],
              assessment: [
                "Quick Quiz: List operations",
                "Code: Implement specified list operations"
              ],
              homework: [
                "Research: When would you use a list vs other data structures?"
              ],
              teacherNotes: [
                "Emphasize that choosing the right data structure matters.",
                "Visual diagrams help understanding."
              ],
              differentiation: [
                "**For struggling learners:** Focus on basic list operations.",
                "**For advanced learners:** Research Python's array module."
              ]
            },
            {
              id: "session2_1.2",
              number: 2,
              title: "Stacks and Queues",
              duration: "45-60 minutes",
              structure: [
                { phase: "Review Lists", time: "10 min" },
                { phase: "Understanding Stacks (LIFO)", time: "15 min" },
                { phase: "Understanding Queues (FIFO)", time: "15 min" },
                { phase: "Practical Implementation", time: "15 min" }
              ],
              introduction: [
                "Review list operations from previous session.",
                "Introduce stacks: Last In, First Out (LIFO) - like a stack of plates.",
                "Introduce queues: First In, First Out (FIFO) - like a line at a shop."
              ],
              mainActivities: [
                {
                  name: "Understanding Stacks",
                  steps: [
                    "Real-world examples: browser back button, undo functionality.",
                    "Operations: push (add to top), pop (remove from top), peek (view top).",
                    "Implement a stack using Python list."
                  ]
                },
                {
                  name: "Understanding Queues",
                  steps: [
                    "Real-world examples: print queue, customer service line.",
                    "Operations: enqueue (add to back), dequeue (remove from front).",
                    "Implement a queue using Python list or collections.deque."
                  ]
                }
              ],
              materials: [
                "Computers with Python",
                "Visual aids for stack/queue operations",
                "Practice problems"
              ],
              assessment: [
                "Demonstrate stack operations",
                "Implement a simple queue application"
              ],
              homework: [
                "Create a program that uses a stack to reverse a string."
              ],
              teacherNotes: [
                "Use physical demonstrations if possible (stack of books, line of students).",
                "Emphasize the difference between LIFO and FIFO."
              ],
              differentiation: [
                "**For struggling learners:** Provide completed stack implementation to study.",
                "**For advanced learners:** Implement a priority queue."
              ]
            }
          ]
        }
      ]
    },
    {
      id: "strand2",
      name: "Strand 2: Database Systems",
      description: "Database design, SQL, and data management",
      color: "green",
      units: [
        {
          id: "unit2.1",
          unit: "Unit 2.1",
          title: "Database Design and SQL Fundamentals",
          weeks: "Weeks 1-5 (Term 2)",
          duration: "10 Sessions (45-60 min each)",
          linkedToScheme: true,
          linkedToBlueprint: true,
          sessions: [
            {
              id: "session1_2.1",
              number: 1,
              title: "Introduction to Databases",
              duration: "45-60 minutes",
              structure: [
                { phase: "What is a Database?", time: "15 min" },
                { phase: "Database Types", time: "15 min" },
                { phase: "Relational Database Concepts", time: "15 min" },
                { phase: "Summary", time: "10 min" }
              ],
              introduction: [
                "Discuss: Where is data stored in organizations?",
                "Examples: School records, bank accounts, social media.",
                "Define database as an organized collection of structured data."
              ],
              mainActivities: [
                {
                  name: "Database Concepts",
                  steps: [
                    "Explain flat files vs databases.",
                    "Introduce DBMS (Database Management System).",
                    "Types: Relational, NoSQL, Object-oriented.",
                    "Focus on relational databases with tables, rows, columns."
                  ]
                },
                {
                  name: "Relational Database Concepts",
                  steps: [
                    "Tables (relations) store data.",
                    "Rows (records/tuples) represent individual entries.",
                    "Columns (fields/attributes) define data types.",
                    "Primary key uniquely identifies each row.",
                    "Foreign key links tables together."
                  ]
                }
              ],
              materials: [
                "Projector",
                "Database diagrams",
                "Sample database examples"
              ],
              assessment: [
                "Discussion participation",
                "Quick quiz on terminology"
              ],
              homework: [
                "Identify 5 real-world databases and their purpose."
              ],
              teacherNotes: [
                "Use local examples: school database, hospital records.",
                "Visual diagrams are essential for understanding relationships."
              ],
              differentiation: [
                "**For struggling learners:** Focus on basic table concept.",
                "**For advanced learners:** Research different DBMS options."
              ]
            },
            {
              id: "session2_2.1",
              number: 2,
              title: "Entity-Relationship Diagrams",
              duration: "45-60 minutes",
              structure: [
                { phase: "Review Database Concepts", time: "10 min" },
                { phase: "ER Diagram Components", time: "20 min" },
                { phase: "Creating ER Diagrams", time: "20 min" },
                { phase: "Summary", time: "5 min" }
              ],
              introduction: [
                "Review tables, primary keys, foreign keys.",
                "Introduce ER diagrams as visual representations of database structure.",
                "Explain the importance of planning before creating a database."
              ],
              mainActivities: [
                {
                  name: "ER Diagram Components",
                  steps: [
                    "Entities: Represented as rectangles (e.g., Student, Teacher, Course).",
                    "Attributes: Represented as ovals connected to entities.",
                    "Relationships: Represented as diamonds (e.g., 'enrolls in', 'teaches').",
                    "Cardinality: One-to-One, One-to-Many, Many-to-Many."
                  ]
                },
                {
                  name: "Creating ER Diagrams",
                  steps: [
                    "Work through example: School database with Students, Teachers, Courses.",
                    "Identify entities and their attributes.",
                    "Determine relationships between entities.",
                    "Draw the complete ER diagram using draw.io or paper."
                  ]
                }
              ],
              materials: [
                "Projector",
                "draw.io or similar tool",
                "ER diagram examples",
                "Paper for hand-drawn diagrams"
              ],
              assessment: [
                "Create ER diagram for a library system",
                "Identify errors in sample ER diagrams"
              ],
              homework: [
                "Design an ER diagram for a local business (shop, clinic, etc.)."
              ],
              teacherNotes: [
                "Start simple and add complexity gradually.",
                "Common mistake: confusing entities and attributes."
              ],
              differentiation: [
                "**For struggling learners:** Provide partially completed diagrams.",
                "**For advanced learners:** Include weak entities and composite attributes."
              ]
            }
          ]
        }
      ]
    },
    {
      id: "strand3",
      name: "Strand 3: Web Development",
      description: "Modern web technologies and responsive design",
      color: "purple",
      units: [
        {
          id: "unit3.1",
          unit: "Unit 3.1",
          title: "HTML5 and CSS3 Advanced Features",
          weeks: "Weeks 1-3 (Term 4)",
          duration: "6 Sessions (45-60 min each)",
          linkedToScheme: true,
          linkedToBlueprint: true,
          sessions: [
            {
              id: "session1_3.1",
              number: 1,
              title: "Review of HTML and CSS Basics",
              duration: "45-60 minutes",
              structure: [
                { phase: "Prior Knowledge Assessment", time: "10 min" },
                { phase: "HTML5 New Elements", time: "15 min" },
                { phase: "CSS3 New Features", time: "15 min" },
                { phase: "Hands-on Practice", time: "15 min" }
              ],
              introduction: [
                "Quick assessment: What HTML/CSS do students already know?",
                "Discuss evolution from HTML4 to HTML5.",
                "Preview what's new in HTML5 and CSS3."
              ],
              mainActivities: [
                {
                  name: "HTML5 New Elements",
                  steps: [
                    "Semantic elements: <header>, <nav>, <main>, <section>, <article>, <footer>.",
                    "Why semantics matter: accessibility, SEO, maintainability.",
                    "New input types: email, date, number, range, color.",
                    "Media elements: <video>, <audio>."
                  ]
                },
                {
                  name: "CSS3 New Features",
                  steps: [
                    "Border radius for rounded corners.",
                    "Box shadows and text shadows.",
                    "Gradients: linear and radial.",
                    "Transitions for smooth animations."
                  ]
                }
              ],
              materials: [
                "Computers with VS Code",
                "Live Server extension",
                "Sample HTML/CSS files"
              ],
              assessment: [
                "Create a semantic HTML page structure",
                "Apply CSS3 features to enhance design"
              ],
              homework: [
                "Redesign an old webpage using HTML5 semantic elements."
              ],
              teacherNotes: [
                "Assess prior knowledge to adjust pacing.",
                "Show before/after examples of HTML5 improvements."
              ],
              differentiation: [
                "**For struggling learners:** Focus on semantic HTML only.",
                "**For advanced learners:** Explore CSS3 animations and keyframes."
              ]
            }
          ]
        }
      ]
    },
    {
      id: "strand4",
      name: "Strand 4: Cybersecurity",
      description: "Security principles, threats, and protection",
      color: "red",
      units: [
        {
          id: "unit4.1",
          unit: "Unit 4.1",
          title: "Cybersecurity Fundamentals",
          weeks: "Weeks 6-10 (Term 3)",
          duration: "10 Sessions (45-60 min each)",
          linkedToScheme: true,
          linkedToBlueprint: true,
          sessions: [
            {
              id: "session1_4.1",
              number: 1,
              title: "Introduction to Cybersecurity",
              duration: "45-60 minutes",
              structure: [
                { phase: "What is Cybersecurity?", time: "15 min" },
                { phase: "Types of Cyber Threats", time: "20 min" },
                { phase: "Case Studies", time: "15 min" },
                { phase: "Summary", time: "5 min" }
              ],
              introduction: [
                "Discuss recent cyber attacks in the news.",
                "Define cybersecurity: protecting systems, networks, and data.",
                "Why is cybersecurity important in today's digital world?"
              ],
              mainActivities: [
                {
                  name: "Types of Cyber Threats",
                  steps: [
                    "Malware: viruses, worms, trojans, ransomware.",
                    "Social engineering: phishing, pretexting, baiting.",
                    "Network attacks: DDoS, man-in-the-middle.",
                    "Insider threats: accidental and malicious."
                  ]
                },
                {
                  name: "Case Studies",
                  steps: [
                    "Analyze real-world cyber attacks (WannaCry, Equifax breach).",
                    "Identify what went wrong and how it could have been prevented.",
                    "Discuss impact on organizations and individuals."
                  ]
                }
              ],
              materials: [
                "Projector",
                "Case study handouts",
                "News articles on recent attacks"
              ],
              assessment: [
                "Discussion participation",
                "Identify threat types in scenarios"
              ],
              homework: [
                "Research a recent cyber attack and write a summary."
              ],
              teacherNotes: [
                "Keep examples current and relevant to students.",
                "Emphasize personal cybersecurity practices."
              ],
              differentiation: [
                "**For struggling learners:** Focus on common threats like phishing.",
                "**For advanced learners:** Research advanced persistent threats (APTs)."
              ]
            }
          ]
        }
      ]
    },
    {
      id: "strand5",
      name: "Strand 5: Networks",
      description: "Computer networking and infrastructure",
      color: "orange",
      units: [
        {
          id: "unit5.1",
          unit: "Unit 5.1",
          title: "Computer Networks Fundamentals",
          weeks: "Weeks 1-5 (Term 3)",
          duration: "10 Sessions (45-60 min each)",
          linkedToScheme: true,
          linkedToBlueprint: true,
          sessions: [
            {
              id: "session1_5.1",
              number: 1,
              title: "Introduction to Computer Networks",
              duration: "45-60 minutes",
              structure: [
                { phase: "What is a Network?", time: "10 min" },
                { phase: "Network Types", time: "20 min" },
                { phase: "Network Topologies", time: "15 min" },
                { phase: "Summary", time: "10 min" }
              ],
              introduction: [
                "Discuss: How do computers communicate with each other?",
                "Define network: Two or more devices connected to share resources.",
                "Examples of networks in daily life."
              ],
              mainActivities: [
                {
                  name: "Network Types",
                  steps: [
                    "LAN (Local Area Network): Small area, high speed.",
                    "WAN (Wide Area Network): Large geographic area.",
                    "MAN (Metropolitan Area Network): City-wide.",
                    "PAN (Personal Area Network): Very small range (Bluetooth)."
                  ]
                },
                {
                  name: "Network Topologies",
                  steps: [
                    "Bus topology: All devices on one cable.",
                    "Star topology: Central hub/switch.",
                    "Ring topology: Circular connection.",
                    "Mesh topology: All devices interconnected.",
                    "Advantages and disadvantages of each."
                  ]
                }
              ],
              materials: [
                "Projector",
                "Topology diagrams",
                "Cisco Packet Tracer (if available)"
              ],
              assessment: [
                "Draw and label network topologies",
                "Match network types to scenarios"
              ],
              homework: [
                "Identify the network type and topology used at your school."
              ],
              teacherNotes: [
                "Use school's own network as an example.",
                "Visual diagrams are essential."
              ],
              differentiation: [
                "**For struggling learners:** Focus on LAN vs WAN and star topology.",
                "**For advanced learners:** Research hybrid topologies."
              ]
            }
          ]
        }
      ]
    }
  ],

  // Assessment Calendar
  assessmentCalendar: [
    { week: 3, term: 1, title: "OOP Programming Test", marks: 30, type: "Practical" },
    { week: 6, term: 1, title: "Data Structures Test", marks: 25, type: "Theory" },
    { week: 10, term: 1, title: "Term 1 Examination", marks: 50, type: "Exam" },
    { week: 5, term: 2, title: "Database Design Project", marks: 35, type: "Practical" },
    { week: 10, term: 2, title: "Term 2 Examination", marks: 50, type: "Exam" },
    { week: 5, term: 3, title: "Networking Practical", marks: 30, type: "Practical" },
    { week: 10, term: 3, title: "Term 3 Examination", marks: 50, type: "Exam" },
    { week: 5, term: 4, title: "Web Development Project", marks: 40, type: "Project" },
    { week: 9, term: 4, title: "Capstone Project", marks: 50, type: "Project" },
    { week: 10, term: 4, title: "Final Examination", marks: 100, type: "Exam" }
  ],

  totalMarks: 460
};

export default year13ComputerStudiesPlanner;
